import { CreditCardIcon, KeyIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { BaseBadge, ErrorBadge, SuccessBadge } from "../components/Badge";
import { Button } from "../components/Button";
import ConfirmationModal from "../components/modals/ConfirmationModal";
import { useAppContext } from "../context/AppContext";
import queryClient, { QueryKey } from "../hooks/state";
import { useUser } from "../hooks/useUsers";
import { Subscription } from "../models/types";
import { changePassword } from "../services/LoginService";
import { cancelSubscription } from "../services/UserService";
import { formatTimestamp, getPlanName } from "../utils/helper";

export default function Profile() {
    const { setNotification } = useAppContext();

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [repeatNewPassword, setRepeatNewPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");
    const [repeatPasswordError, setRepeatPasswordError] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
    const [reason, setReason] = useState<string>("");
    const { data: user, isLoading: userLoading } = useUser();
    const subscription: Subscription | undefined = user?.subscriptions?.[0];

    useEffect(() => {
        setFirstName(user?.first_name || "");
        setLastName(user?.last_name || "");
    }, [user]);

    const handleCheckPassword = ({ onChange, value }: { onChange: (val: string) => void; value: string }) => {
        if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(value)) {
            setNewPasswordError("A senha deve conter pelo menos 8 caracteres, uma letra e um número");
        } else {
            setNewPasswordError("");
        }
        setPasswordError("");
        onChange(value);
    }

    const handleChangeRepeatPassword = (value: string) => {
        setRepeatNewPassword(value);
        setPasswordError("");
        if (newPassword !== value) {
            setRepeatPasswordError("As senhas não coincidem");
        } else {
            setRepeatPasswordError("");
        }
    }

    const handleUpdatePassword = async () => {
        try {
            if (!currentPassword) {
                setPasswordError("Informe sua senha atual");
                return;
            }
            if (newPassword !== repeatNewPassword) {
                setPasswordError("As senhas não coincidem");
                return;
            }
            if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(newPassword)) {
                setPasswordError("A senha deve conter pelo menos 8 caracteres, uma letra e um número");
                return;
            }
            setPasswordError("");
            await changePassword({ old_password: currentPassword, new_password: newPassword });
            setNewPassword("");
            setCurrentPassword("");
            setRepeatNewPassword("");
            setNotification({ message: "Senha atualizada com sucesso", type: "success", title: "Sucesso" });
        } catch (e) {
            setNotification({ message: "Erro ao atualizar senha, verifique os dados informados", type: "error", title: "Erro" });
        }
    }

    const handleCancelSubscription = async () => {
        if (!subscription?.id) return;

        try {
            setIsLoading(true);
            await cancelSubscription(subscription.id, reason);
            queryClient.invalidateQueries(QueryKey.users);
            setNotification({
                message: "Sua assinatura foi cancelada com sucesso",
                type: "success",
                title: "Assinatura Cancelada"
            });
            setConfirmModalOpen(false);
        } catch (error) {
            setNotification({
                message: "Ocorreu um erro ao cancelar sua assinatura. Por favor, tente novamente.",
                type: "error",
                title: "Erro"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBadge = () => {
        if (!subscription) return <BaseBadge title="Plano Gratuito" />;

        if (subscription.status === 'trialing') {
            return <BaseBadge title="Período de Teste" color="text-blue-900" />;
        } else if (subscription.status === 'active') {
            return <SuccessBadge title="Ativo" />;
        } else {
            return <ErrorBadge title="Inativo" />;
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-row w-full gap-6 flex-wrap-reverse md:flex-nowrap">
                <div className="flex flex-col gap-4 w-full">
                    {/* Informações Pessoais */}
                    <div className="shadow-md flex rounded-lg p-6 flex-col gap-4 bg-primaryBg border border-primaryBorder">
                        <div className="flex flex-row items-center gap-2 border-b border-primaryBorder pb-4">
                            <UserCircleIcon className="h-6 w-6 min-w-6 min-h-6 text-primary bg-gray-100 text-gray-600 p-1 rounded-md" />
                            <h1 className="text-primary font-primary text-primaryText">Informações Pessoais</h1>
                        </div>

                        <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-text font-text text-thirdText">Primeiro nome</label>
                                    <input
                                        type="text"
                                        disabled
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className="w-full border border-secondaryBorder rounded-md px-3 py-2 bg-primaryBg text-text font-text"
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-text font-text text-thirdText">Sobrenome</label>
                                    <input
                                        type="text"
                                        disabled
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        className="w-full border border-secondaryBorder rounded-md px-3 py-2 bg-primaryBg text-text font-text"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-text font-text text-thirdText">E-mail</label>
                                <input
                                    disabled
                                    value={user?.email}
                                    type="text"
                                    className="w-full border border-secondaryBorder rounded-md px-3 py-2 bg-primaryBg text-text font-text"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Detalhes da Assinatura */}
                    <div className="shadow-md flex rounded-lg p-6 flex-col gap-4 bg-primaryBg border border-primaryBorder">
                        <div className="flex flex-row items-center justify-between border-b border-primaryBorder pb-4">
                            <div className="flex flex-row items-center gap-2">
                                <CreditCardIcon className="h-6 w-6 min-w-6 min-h-6 text-primary bg-gray-100 text-gray-600 p-1 rounded-md" />
                                <h1 className="text-primary font-primary text-primaryText">Detalhes da Assinatura</h1>
                            </div>
                            {getStatusBadge()}
                        </div>

                        <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-text font-text text-thirdText">Plano atual</label>
                                    <div className="w-full border border-secondaryBorder rounded-md px-3 py-2 bg-primaryBg text-primary font-primary">
                                        {subscription ? getPlanName(subscription.plan.id) : 'Plano Gratuito'}
                                    </div>
                                </div>

                                {subscription && !subscription.canceled && <div className="flex flex-col gap-2">
                                    <label className="text-text font-text text-thirdText">Próxima cobrança</label>
                                    <div className="w-full border border-secondaryBorder rounded-md px-3 py-2 bg-primaryBg text-primary font-primary">
                                        {subscription ? formatTimestamp(subscription.current_period_end) : 'N/A'}
                                    </div>
                                </div>}
                            </div>

                            {subscription?.status === 'trialing' && (
                                <div className="flex flex-col gap-4 border border-secondaryBorder rounded-md p-4">
                                    <h3 className="text-primary font-primary text-primaryText">Período de Teste</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1">
                                            <div className="text-small font-small text-thirdText">Início</div>
                                            <div className="text-text font-text">{formatTimestamp(subscription.trial_start)}</div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <div className="text-small font-small text-thirdText">Término</div>
                                            <div className="text-text font-text">{formatTimestamp(subscription.trial_end)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {subscription && !subscription.canceled && (
                                <div className="w-full flex justify-end">
                                    <Button
                                        title="Cancelar Assinatura"
                                        onClick={() => setConfirmModalOpen(true)}
                                        className="bg-white text-red-600 hover:bg-red-50 border border-red-300 rounded-2xl"
                                        loading={isLoading}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Alterar Senha */}
                    <div className="shadow-md flex rounded-lg p-6 flex-col gap-4 bg-primaryBg border border-primaryBorder">
                        <div className="flex flex-row items-center gap-2 border-b border-primaryBorder pb-4">
                            <KeyIcon className="h-6 w-6 min-w-6 min-h-6 text-primary bg-gray-100 text-gray-600 p-1 rounded-md" />
                            <h1 className="text-primary font-primary text-primaryText">Alterar Senha</h1>
                        </div>

                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col gap-2">
                                <label className="text-text font-text text-thirdText">Senha atual</label>
                                <input
                                    value={currentPassword}
                                    onChange={(e) => {
                                        setPasswordError("");
                                        setCurrentPassword(e.target.value)
                                    }}
                                    type="password"
                                    className="w-full border border-secondaryBorder rounded-md px-3 py-2 text-text font-text"
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-text font-text text-thirdText">Nova senha</label>
                                <input
                                    value={newPassword}
                                    onChange={(e) => handleCheckPassword({ onChange: setNewPassword, value: e.target.value })}
                                    type="password"
                                    className="w-full border border-secondaryBorder rounded-md px-3 py-2 text-text font-text"
                                />
                                {newPasswordError && <div className="text-red-500 text-small">{newPasswordError}</div>}
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-text font-text text-thirdText">Repetir nova senha</label>
                                <input
                                    value={repeatNewPassword}
                                    onChange={(e) => handleChangeRepeatPassword(e.target.value)}
                                    type="password"
                                    className="w-full border border-secondaryBorder rounded-md px-3 py-2 text-text font-text"
                                />
                                {repeatPasswordError && <div className="text-red-500 text-small">{repeatPasswordError}</div>}
                            </div>

                            {passwordError && <div className="text-red-500 text-small">{passwordError}</div>}

                            <div className="w-full flex justify-end">
                                <Button
                                    title="Atualizar Senha"
                                    onClick={handleUpdatePassword}
                                    disabled={currentPassword === "" || newPassword === "" || repeatNewPassword === ""}
                                    className="bg-blue-600 hover:bg-blue-700 rounded-2xl text-white disabled:bg-gray-300 disabled:hover:bg-gray-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleCancelSubscription}
                onCancel={() => setConfirmModalOpen(false)}
                title="Cancelar Assinatura"
                setReason={setReason}
                reason={reason}
                message="Tem certeza que deseja cancelar sua assinatura? Você perderá acesso aos recursos premium ao final do período já pago."
            />
        </div>
    );
}